import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createUserWithEmailAndPassword, signOut, updateProfile } from 'firebase/auth';
import { auth } from '../../firebase';
import { useAuth } from './AuthProvider';

const BLOCKED_DOMAINS_URL = '/blocked_domains.csv';

function parseBlockedDomains(csvText) {
  return new Set(
    csvText
      .split(/\r?\n/)
      .map(line => line.trim().toLowerCase())
      .filter(Boolean)
  );
}

function getEmailDomain(value) {
  const normalizedEmail = value.trim().toLowerCase();
  const atIndex = normalizedEmail.lastIndexOf('@');

  if (atIndex === -1 || atIndex === normalizedEmail.length - 1) {
    return '';
  }

  return normalizedEmail.slice(atIndex + 1);
}

export default function Signup() {
  const { user, approved } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [blockedDomains, setBlockedDomains] = useState(() => new Set());
  const [blockedDomainsLoaded, setBlockedDomainsLoaded] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signupCompleted, setSignupCompleted] = useState(false);

  useEffect(() => {
    if (signupCompleted) return;
    if (user && approved) {
      navigate('/dashboard');
    }
  }, [user, approved, navigate, signupCompleted]);

  useEffect(() => {
    let isMounted = true;

    async function loadBlockedDomains() {
      try {
        const response = await fetch(BLOCKED_DOMAINS_URL);

        if (!response.ok) {
          throw new Error('Blocked email domains could not be loaded.');
        }

        const csvText = await response.text();

        if (isMounted) {
          setBlockedDomains(parseBlockedDomains(csvText));
          setBlockedDomainsLoaded(true);
        }
      } catch (loadError) {
        if (isMounted) {
          setError(loadError.message || 'Blocked email domains could not be loaded.');
        }
      }
    }

    loadBlockedDomains();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSignup = async event => {
    event?.preventDefault();
    setError('');
    setSuccess('');

    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedPassword = password.trim();

    if (!trimmedFirstName || !trimmedLastName || !trimmedEmail || !trimmedPassword) {
      setError('First name, last name, email, and password are required.');
      return;
    }

    if (!blockedDomainsLoaded) {
      setError('Email validation is still loading. Please try again.');
      return;
    }

    const emailDomain = getEmailDomain(trimmedEmail);
    if (!emailDomain) {
      setError('Please enter a valid email address.');
      return;
    }

    if (blockedDomains.has(emailDomain)) {
      setError('Please use a business email address. That email domain is not allowed.');
      return;
    }

    setIsSubmitting(true);
    try {
      await createUserWithEmailAndPassword(auth, trimmedEmail, trimmedPassword);

      if (!auth.currentUser) {
        throw new Error('Signup completed but no authenticated user was found.');
      }

      await updateProfile(auth.currentUser, {
        displayName: `${trimmedFirstName} ${trimmedLastName}`.trim(),
      });

      const token = await auth.currentUser.getIdToken(true);
      const getUtm = (key) => searchParams.get(key) ?? sessionStorage.getItem(key) ?? undefined;
      const utmPayload = {
        utmCampaign: getUtm('utm_campaign'),
        utmSource: getUtm('utm_source'),
        utmMedium: getUtm('utm_medium'),
        utmTerm: getUtm('utm_term'),
        utmContent: getUtm('utm_content'),
      };
      console.log('[Signup] UTM params:', utmPayload);
      console.log('[Signup] Full URL:', window.location.href);
      console.log('[Signup] Search params string:', window.location.search);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/ecommerce/trial/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          firstName: trimmedFirstName,
          lastName: trimmedLastName,
          pageName: 'Signup',
          pageUri: window.location.href,
          ...utmPayload,
        }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || 'Account created, but trial setup failed.');
      }

      setSuccess('Account created and trial activated.');
      setSignupCompleted(true);
      await signOut(auth);
      setFirstName('');
      setLastName('');
      setEmail('');
      setPassword('');
    } catch (signupError) {
      const message = signupError?.message || 'Unable to create account. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 shadow-2xl rounded-2xl overflow-hidden bg-white">
          <div className="px-8 py-12 sm:px-12 lg:px-16">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-6">
                <img
                  src="/SmartSalesRouting.svg"
                  alt="SalesFlow Logo"
                  className="h-16 w-auto"
                />
              </div>
              <p className="text-gray-600 text-lg">Create your Distributely account</p>
            </div>

            {signupCompleted ? (
              <div className="space-y-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-green-800 font-semibold text-lg">Account Created</p>
                  <p className="text-green-700 text-sm mt-1">
                    Your trial is active. Continue to login.
                  </p>
                </div>
                <button
                  onClick={() => navigate('/')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition duration-200"
                >
                  Click Here to Login
                </button>
              </div>
            ) : (
              <form className="space-y-6" onSubmit={handleSignup}>
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                    {error}
                  </div>
                )}

                {success ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
                    {success}
                  </div>
                ) : null}

                <div>
                  <label htmlFor="signup-first-name" className="block text-sm font-medium text-gray-700 mb-2">
                    First Name
                  </label>
                  <input
                    id="signup-first-name"
                    type="text"
                    placeholder="Enter your first name"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition duration-200"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="signup-last-name" className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    id="signup-last-name"
                    type="text"
                    placeholder="Enter your last name"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition duration-200"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    id="signup-email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition duration-200"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="signup-password"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Password
                  </label>
                  <input
                    id="signup-password"
                    type="password"
                    placeholder="Create a password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition duration-200"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || Boolean(success)}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-3 px-4 rounded-lg transition duration-200"
                >
                  {isSubmitting ? 'Creating Account...' : 'Create Account'}
                </button>

                <div className="text-center">
                  <span className="text-gray-600 text-sm">Already have an account? </span>
                  <button
                    onClick={() => navigate('/')}
                    className="text-blue-600 hover:text-blue-700 font-medium text-sm transition duration-200"
                  >
                    Sign In
                  </button>
                </div>
              </form>
            )}
          </div>

          <div className="bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 px-8 py-12 sm:px-12 lg:px-16 flex items-center">
            <div className="w-full text-center text-white">
              <h2 className="text-4xl font-bold mb-4 leading-tight">
                Start your 30-day
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-cyan-300">
                  free trial
                </span>
              </h2>
              <p className="text-xl text-blue-100 leading-relaxed mb-10">
                Create your account, explore the full platform, and add billing details later in settings.
              </p>
              <div className="space-y-4 text-left">
                <p className="text-blue-100">Full access during trial</p>
                <p className="text-blue-100">No card required to start</p>
                <p className="text-blue-100">In-app billing setup when ready</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
